import { Checkbox } from '@/src/components/Antd';
import CommonHeader from '@/src/components/CommonHeader';
import CommonFormItem from '@/src/pages/Settings/Logistics/Components/Common';
import {
  getPickupLocation,
  setPickupLocation,
} from '@/src/request/settings/logistics/pickupLocation';
import {
  isUserValid,
  storeResourceNameGenerator,
} from '@/src/tools/getUserInfo';
import {
  EPermissionSource,
  EnumCategory,
  EnumOperation,
  EnumSubCategory,
} from '@/src/types/common/resources';
import { useRequest } from 'ahooks';
import { Form, Spin } from 'antd';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet } from 'src/tools/tools';
import { breadcrumbData } from '../config';
import styles from '../style.module.less';
import { weekList } from './Components/PickUpTime';
import TimeZone from './Components/TimeZone';

const permissions = () => {
  const add_edit_click_and_collect_pickup_location = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Settings,
        subCategory: EnumSubCategory.SettingsLogisticsPickupLocations,
        operation: EnumOperation.add_edit_click_and_collect_pickup_location,
      }),
    ],
  });
  const view_click_and_collect_pickup_location_details = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Settings,
        subCategory: EnumSubCategory.SettingsLogisticsPickupLocations,
        operation: EnumOperation.view_click_and_collect_pickup_location_details,
      }),
    ],
  });
  return {
    add_edit_click_and_collect_pickup_location,
    view_click_and_collect_pickup_location_details,
  };
};

const PickupLocationSetting = () => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const {
    add_edit_click_and_collect_pickup_location,
    view_click_and_collect_pickup_location_details,
  } = permissions();
  const history = useHistory();
  const [form] = Form.useForm();
  const { uuid }: any = useParams();
  const [onSaveDisable, setOnSaveDisable] = useState<boolean>(true);
  const { loading: setPickupLocationLoading, run: _setPickupLocation } =
    useRequest((params) => setPickupLocation(params), {
      manual: true,
      onSuccess(result) {
        history.push('/settings/logistics');
      },
    });
  const { loading: getPickupLocationLoading, run: _getPickupLocation } =
    useRequest((uuid) => getPickupLocation(uuid), {
      manual: true,
      ready: view_click_and_collect_pickup_location_details,
      onSuccess(result) {
        // setTitle(result.locationName);
        // setIsDefault(result.defaultLocation);
        form.setFieldsValue({
          defaultWeightUnit: result.defaultWeightUnit,
          locationName: result.locationName,
          areaCode: result.areaCode,
          areaCodeInfo: result.areaCodeInfo,
          phone: result.phone,
          postcode: result.postcode || '',
          province: result.province,
          country: result.country,
          latitude: result.latitude || '',
          longitude: result.longitude || '',
          defaultLocation: result.defaultLocation,
          addressLine: result.addressLine,
          type: result.pickupAvailableTime.type,
          customizes: result.pickupAvailableTime.customizes,
          blockService: result.pickupAvailableTime.blockService,
          blockOutDates: result.pickupAvailableTime.blockOutDates,
          requireLocalNumber: result.requireLocalNumber,
        });
      },
    });
  useEffect(() => {
    if (uuid) {
      _getPickupLocation(uuid);
    }
  }, []);
  const onValuesChange = () => {
    if (onSaveDisable) {
      setOnSaveDisable(!onSaveDisable);
    }
  };
  const onFormSubmit = () => {
    form.validateFields().then((res) => {
      const params = res;
      const customizesData = (res.customizes || []).map((item, index) => ({
        ...item,
        name: weekList({ getI18n })[index].name,
      }));
      params.pickupAvailableTime = {
        type: res.type,
        customizes: customizesData,
        blockService: res.blockService,
        blockOutDates: res.blockOutDates,
      };
      if (uuid) {
        params.uuid = uuid;
      }
      _setPickupLocation(params);
    });
  };
  const goBackToList = () => {
    history.push(`/settings/logistics`);
  };

  return (
    <div className={styles.pickupLocationsContainer}>
      <Spin spinning={getPickupLocationLoading || setPickupLocationLoading}>
        <Form
          layout={'vertical'}
          form={form}
          className={styles.editForm}
          onValuesChange={onValuesChange}>
          <CommonHeader
            breadcrumbData={breadcrumbData({ getI18n })}
            onSaveDisable={
              onSaveDisable || !add_edit_click_and_collect_pickup_location
            }
            onSubmit={onFormSubmit}
            onDiscard={goBackToList}
            onGoBackClick={goBackToList}
          />
          <div className={styles.commonContainer}>
            <div className={styles.itemBlock}>
              <div className={styles.header}>
                <div className={styles.title}>
                  {uuid
                    ? getI18n(
                        'storeLogistics.pickupLocations.editPickupLocation',
                      )
                    : getI18n(
                        'storeLogistics.pickupLocations.addPickupLocation',
                      )}
                </div>
              </div>
              <div className={styles.content}>
                <CommonFormItem
                  form={form}
                  from='pickupLocation'
                  onEditable={true}
                />
              </div>
            </div>
            <div className={styles.itemBlock}>
              <div className={styles.header}>
                <div className={styles.title}>
                  {getI18n(
                    'storeLogistics.pickupLocations.recipientInfoSettings',
                  )}
                </div>
              </div>
              <div className={styles.content}>
                <Form.Item
                  initialValue={false}
                  noStyle
                  name='requireLocalNumber'
                  valuePropName='checked'
                  className={styles.courierAccount}>
                  <Checkbox>
                    {getI18n(
                      'storeLogistics.pickupLocations.requireLocalNumberLabel',
                    )}
                  </Checkbox>
                </Form.Item>
              </div>
            </div>
            <div className={styles.itemBlock}>
              <div className={styles.header}>
                <div className={styles.title}>
                  {getI18n(
                    'storeLogistics.pickupLocations.availableTimeLocalTimeZone',
                  )}
                </div>
              </div>
              <div className={styles.content}>
                <TimeZone />
              </div>
            </div>
          </div>
        </Form>
      </Spin>
    </div>
  );
};

export default PickupLocationSetting;
